import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { eventsService } from '../../../core/services/eventsService';
import { EventDocumentTemplate, EventLocation, EventType } from '../../../types';
import styles from './SchedulerSetting.module.scss';
import EventLocationsList from './EventsSetting/EventLocationsList';
import EventTypesList from './EventsSetting/EventTypesList';
import CreateEventLocation from './EventsSetting/CreateEventLocation';
import CreateEventType from './EventsSetting/CreateEventType';
import EditEventLocation from './EventsSetting/EditEventLocation';
import EditEventType from './EventsSetting/EditEventType';
import BanquetEventOrderList from './EventsSetting/BanquetEventOrderList';
import UploadBanquetEventOrder from './EventsSetting/UploadBanquetEventOrder';
import EditBanquetEventOrder from './EventsSetting/EditBanquetEventOrder';
import { CircularProgress, Box } from '@mui/material';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import GamemasterUserDevices from './GamemasterSetting/Pages/UserDevices/GamemasterUserDevices';

type ViewMode =
	| 'list'
	| 'createEventLocation'
	| 'createEventType'
	| 'editEventLocation'
	| 'editEventType'
	| 'createBanquetDocument'
	| 'editBanquetDocument';

type DeviceViewMode = 'list' | 'create' | 'show' | 'edit';

const EventsSetting = () => {
	const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
	const canViewEvents = hasViewPermission('Events');
	const canEditEvents = hasEditPermission('Events');
	const canDeleteEvents = hasDeletePermission('Events');
	const canExecuteEvents = hasExecutePermission('Events');
	const [viewMode, setViewMode] = useState<ViewMode>('list');
	const [eventLocations, setEventLocations] = useState<EventLocation[]>([]);
	const [eventTypes, setEventTypes] = useState<EventType[]>([]);
	const [editingEventLocation, setEditingEventLocation] = useState<EventLocation | null>(null);
	const [editingEventType, setEditingEventType] = useState<EventType | null>(null);
	const [eventDocumentTemplates, setEventDocumentTemplates] = useState<EventDocumentTemplate[]>([]);
	const [editingBanquetDocument, setEditingBanquetDocument] = useState<EventDocumentTemplate | null>(null);

	const [deviceViewMode, setDeviceViewMode] = useState<DeviceViewMode>('list');

	const [loadingLocations, setLoadingLocations] = useState(false);
	const [loadingTypes, setLoadingTypes] = useState(false);
	const [loadingBanquetDocs, setLoadingBanquetDocs] = useState(false);

	useEffect(() => {
		if (canViewEvents) {
			loadData();
		}
	}, [canViewEvents]);

	const loadData = async () => {
		setLoadingLocations(true);
		setLoadingTypes(true);
		setLoadingBanquetDocs(true);

		try {
			const [locationsData, typesData, banquetData] = await Promise.all([
				eventsService.getEventLocations(),
				eventsService.getEventTypes(),
				eventsService.getEventDocumentTemplates(),
			]);
			setEventLocations(locationsData || []);
			setEventTypes(typesData || []);
			setEventDocumentTemplates(banquetData || []);
			setViewMode('list');
		} finally {
			setLoadingLocations(false);
			setLoadingTypes(false);
			setLoadingBanquetDocs(false);
		}
	};

	const handleBackClick = () => {
		setViewMode('list');
		setEditingEventLocation(null);
		setEditingEventType(null);
		setEditingBanquetDocument(null);
	};

	if (!canViewEvents) {
		return (
			<div className={styles.schedulerSettings}>
				<Card className="card">
					<div style={{ padding: '20px', textAlign: 'center' }}>
						You do not have permission to view events settings.
					</div>
				</Card>
			</div>
		);
	}

	if (viewMode === 'createEventLocation') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateEventLocation backButtonClick={handleBackClick} onEventLocationCreated={loadData} />
			</div>
		);
	}

	if (viewMode === 'editEventLocation' && editingEventLocation) {
		return (
			<div className={styles.schedulerSettings}>
				<EditEventLocation
					location={editingEventLocation}
					handleBackClick={handleBackClick}
					onEventLocationUpdated={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'createEventType') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateEventType backButtonClick={handleBackClick} onEventTypeCreated={loadData} />
			</div>
		);
	}

	if (viewMode === 'editEventType' && editingEventType) {
		return (
			<div className={styles.schedulerSettings}>
				<EditEventType
					type={editingEventType}
					handleBackClick={handleBackClick}
					onEventTypeUpdated={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'createBanquetDocument') {
		return (
			<div className={styles.schedulerSettings}>
				<UploadBanquetEventOrder
					existingDocumentsCount={eventDocumentTemplates.length}
					existingNames={eventDocumentTemplates.map((d) => d.name)}
					handleBackClick={handleBackClick}
					onDocumentUploaded={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'editBanquetDocument' && editingBanquetDocument) {
		return (
			<div className={styles.schedulerSettings}>
				<EditBanquetEventOrder
					document={editingBanquetDocument}
					existingNames={eventDocumentTemplates
						.map((d) => d.name)
						.filter((n) => n !== editingBanquetDocument.name)}
					handleBackClick={handleBackClick}
					onDocumentUpdated={loadData}
				/>
			</div>
		);
	}

	return (
		<div className={styles.schedulerSettings}>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Event Locations</h2>
					<Button
						className={styles.addButton}
						onClick={() => setViewMode('createEventLocation')}
						disabled={!canExecuteEvents}
						title={canExecuteEvents ? 'Add Event Location' : 'You do not have permission to add'}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingLocations ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '200px',
							}}
						>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<EventLocationsList
							eventLocations={eventLocations}
							onEventLocationCreated={loadData}
							onEditClick={(location) => {
								if (!canEditEvents) {
									return;
								}
								setEditingEventLocation(location);
								setViewMode('editEventLocation');
							}}
							canEdit={canEditEvents}
							canDelete={canDeleteEvents}
						/>
					)}
				</div>
			</Card>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Event Types</h2>
					<Button
						className={styles.addButton}
						onClick={() => setViewMode('createEventType')}
						disabled={!canExecuteEvents}
						title={canExecuteEvents ? 'Add Event Type' : 'You do not have permission to add'}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingTypes ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '200px',
							}}
						>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<EventTypesList
							eventTypes={eventTypes}
							onEventTypeCreated={loadData}
							onEditClick={(type) => {
								if (!canEditEvents) {
									return;
								}
								setEditingEventType(type);
								setViewMode('editEventType');
							}}
							canEdit={canEditEvents}
							canDelete={canDeleteEvents}
						/>
					)}
				</div>
			</Card>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Banquet Event Order</h2>
					<Button
						className={styles.addButton}
						onClick={() => setViewMode('createBanquetDocument')}
						disabled={!canExecuteEvents || eventDocumentTemplates.length >= 50}
						title={
							!canExecuteEvents
								? 'You do not have permission to add'
								: eventDocumentTemplates.length >= 50
									? 'Maximum of 50 documents reached'
									: 'Add banquet event order PDF'
						}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingBanquetDocs ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '200px',
							}}
						>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<BanquetEventOrderList
							documents={eventDocumentTemplates}
							onRefresh={loadData}
							onEditClick={(doc) => {
								if (!canEditEvents) {
									return;
								}
								setEditingBanquetDocument(doc);
								setViewMode('editBanquetDocument');
							}}
							canEdit={canEditEvents}
							canDelete={canDeleteEvents}
						/>
					)}
				</div>
			</Card>
			<Card className="card" style={{ display: deviceViewMode !== 'list' ? 'block' : undefined }}>
				<GamemasterUserDevices
					onViewModeChange={setDeviceViewMode}
					canEdit={canEditEvents}
					canDelete={canDeleteEvents}
					canExecute={canExecuteEvents}
				/>
			</Card>
		</div>
	);
};

export default EventsSetting;
